import {format} from 'date-fns'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {
  DataObjectId,
  Hub,
  idFromUserEvent,
  noBoardContainerId,
  ObjectType,
  StorageDepartmentHierarchy,
  UserEvent
} from 'yougile-model'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {UserAvatarV2} from '@components/common/user-avatar-v2/user-avatar-v2'
import {FilesPreview} from '@components/statistics/stats-timeline/item/files-preview'
import {ActionIcon} from '@components/statistics/stats-timeline/utils/action-icons/action-icons'
import {getActionNameChangeCustom} from '@components/statistics/stats-timeline/utils/get-action-name-change-custom'
import {getActionNameChangeDeadline} from '@components/statistics/stats-timeline/utils/get-action-name-change-deadline'
import {getActionNameChangeExecutor} from '@components/statistics/stats-timeline/utils/get-action-name-change-executor'
import {getActionNameChangeRepeatTemplate} from '@components/statistics/stats-timeline/utils/get-action-name-change-repeat-temlate'
import {getActionNameChangeStopwatch} from '@components/statistics/stats-timeline/utils/get-action-name-change-stopwatch'
import {getActionNameChangeTimer} from '@components/statistics/stats-timeline/utils/get-action-name-change-timer'
import {getActionNameChangeTimeTracking} from '@components/statistics/stats-timeline/utils/get-action-name-change-timetracking'
import {
  getHint,
  getTitle
} from '@components/statistics/stats-timeline/utils/utils'
import {noPropagate} from '@containers/hooks'
import {UserProxy} from '@extensions/proxies/user-proxy'
import {SubtaskUtil} from '@extensions/subtask-util'
import FileSVG from './file.svg'

interface Props {
  user: UserProxy
  time: number
  event: UserEvent
  hierarchy: StorageDepartmentHierarchy
}

const TwTime = tw.div`
  flex
  mr-12
  leading-6
  text-11
  text-third-old
`

const TwContent = tw.div`
  p-12
  border
  border-l-0
  border-t-0
  border-r-0
  border-solid
  border-main-old
  flex
`

const TwText = tw.div<{$event: UserEvent}>`
  ${({$event}) => ($event.type === 'delete' ? 'text-error-old' : '')}
  flex
  w-full
  text-12
  flex-wrap
  break-words
  overflow-hidden
  text-ellipsis
  min-w-0
`

export const StatisticsTile = (props: Props) => {
  const {user, event, time, hierarchy} = props

  const box = Sync.getCurrentSandbox()
  const obj = box.getHub(idFromUserEvent(event.obj))
  if (!obj) {
    return null
  }
  const objType = box.getType(obj)

  return (
    <TwContent>
      <div>
        <TwTime>{format(time, 'HH:mm')}</TwTime>
      </div>
      <div>
        <div className="flex">
          <ActionIcon event={event} />
        </div>
      </div>
      <div className="flex w-full min-w-0 ml-8">
        <div className="flex justify-between flex-1 min-w-0">
          <div className="flex items-center min-w-0 leading-6">
            <TwText $event={event}>
              <div
                className={`flex min-w-0 ${
                  event.type === 'file' ? 'flex-col' : ''
                }`}
              >
                <div className="flex flex-wrap min-w-0">
                  <div className="mr-4 overflow-hidden text-ellipsis">
                    {getActionName({
                      event,
                      objType,
                      obj
                    })}
                  </div>
                  <ObjectName event={event} obj={obj} hierarchy={hierarchy} />
                </div>
                <FilesPreview event={event} />
              </div>
            </TwText>
          </div>
          <div className="mt-2 ml-6 text-12">
            {user && (
              <UserAvatarV2
                {...user}
                userInfo={`${Lang.msg.statsTimeline.actionWasTakenBy}:\n${user.userName}`}
                hideStatus
                size={20}
              />
            )}
          </div>
        </div>
      </div>
    </TwContent>
  )
}

const extractText = (text: string) => {
  return text.replace(/\/root\/#file:\/user-data\/[^ \n\t]+/g, '').trim()
}

interface GetNamesProps {
  users: DataObjectId[]
}

const GetNames = ({users}: GetNamesProps) => {
  const box = Sync.getCurrentSandbox()
  return (
    <>
      {users
        .map(user => UserProxy.create(box.getUser(user)))
        .map(user => {
          return (
            <div className="flex break-all">
              <UserAvatarV2 {...user} hideStatus size={20} />
              <div className="ml-4 mr-6">{user.userName}</div>
            </div>
          )
        })}
    </>
  )
}

interface ObjectNameProps {
  event: UserEvent
  obj: Hub
  hierarchy: StorageDepartmentHierarchy
}
// eslint-disable-next-line max-lines-per-function
const ObjectName = ({event, obj, hierarchy}: ObjectNameProps) => {
  switch (event.type) {
    case 'message': {
      const m = obj.chat.messages.get(event.time)
      const text = m ? extractText(m.text) : ''
      return <div>“{Util.limitStr(text, 100)}”</div>
    }
    case 'file': {
      return (
        <div className="flex flex-wrap">
          {event.urls.map(url => {
            return (
              <div className="flex mr-6" key={url}>
                <div className="flex text-action-default">
                  <FileSVG />
                </div>
                <a
                  href={Backend.toDataUrl(url)}
                  target="_blank"
                  onClick={noPropagate}
                >
                  <div title={url.split('/').pop().split('?')[0]}>
                    {url.split('/').pop().split('?')[0]}
                  </div>
                </a>
              </div>
            )
          })}
        </div>
      )
    }
    case 'move': {
      return <div>{getHint({event, obj}, hierarchy)}</div>
    }
    case 'rename': {
      const fromTo = Lang.msg.statsTimeline.fromTo(event.oldName, event.newName)
      return <div>{fromTo}</div>
    }
    case 'users':
    case 'add-user-to-project':
    case 'remove-user-from-project': {
      return (
        <div>
          <div className="w-6" />
          <GetNames users={event.users} />
        </div>
      )
    }
    case 'change-project-roles': {
      return <div>{event.roleNames.join(', ')}</div>
    }
    case 'change-project-permissions': {
      return (
        <div>
          <div className="w-6" />
          <GetNames users={event.users} />
        </div>
      )
    }
    case 'add':
    case 'delete':
    case 'restore':
    case 'complete':
    case 'uncomplete':
    case 'done':
    case 'undone':
    case 'timer':
    case 'timetracking':
    case 'stopwatch':
    case 'executor':
    case 'string':
    case 'sprint':
    case 'numeric':
    case 'text':
    case 'deadline':
    case 'deadlineFailed': {
      return <></>
    }
    default: {
      return <div>“{getTitle(obj)}”</div>
    }
  }
}

interface ActionNameProps {
  event: UserEvent
  objType: ObjectType
  obj: Hub
}
const getActionName = ({event, objType, obj}: ActionNameProps) => {
  if (!obj) {
    return ''
  }
  switch (event.type) {
    case 'add':
    case 'delete':
    case 'rename':
    case 'restore':
    case 'move': {
      if (event.type === 'add' || event.type === 'delete') {
        if (
          obj.id === noBoardContainerId ||
          obj.location === noBoardContainerId
        ) {
          const parents = SubtaskUtil.getParentTaskIds(obj.id)
          if (parents?.length) {
            return ` ${Util.capitalize(
              Lang.msg.statsTimeline.subtask
            )} ${Lang.findMsg(
              'statAction' + Util.capitalize(event.type) + 'Other'
            ).toLowerCase()}`
          }
        }
      }
      return `${Util.capitalize(
        Lang.findMsg('statActionObj' + Util.capitalize(objType))
      )} ${Lang.findMsg(
        'statAction' + Util.capitalize(event.type) + 'Other'
      ).toLowerCase()} `
    }
    case 'complete': {
      if (objType === 'project') {
        return Lang.msg.statsTimeline.statActionCompleteProject
      }
      return Lang.msg.statsTimeline.statActionCompleteTask
    }
    case 'uncomplete': {
      if (objType === 'project') {
        return Lang.msg.statsTimeline.statActionUncompleteProject
      }
      return Lang.msg.statsTimeline.statActionUncompleteTask
    }
    case 'message': {
      return Lang.msg.statsTimeline.message
    }
    case 'file': {
      if (event.urls.length > 1) {
        return Lang.msg.statsTimeline.statActionAttachedFiles
      } else {
        return Lang.msg.statsTimeline.statActionAttachedFile
      }
    }
    case 'done': {
      return `${Lang.msg.statActionDone}`
    }
    case 'undone': {
      return `${Lang.msg.statActionUnDone}`
    }
    case 'text':
    case 'numeric':
    case 'string':
    case 'sprint': {
      return getActionNameChangeCustom(event)
    }
    case 'executor': {
      return getActionNameChangeExecutor(event)
    }
    case 'deadline': {
      return getActionNameChangeDeadline(event)
    }

    case 'deadlineFailed': {
      return Lang.msg.statsTimeline.statActionDeadlineFailed
    }

    case 'timetracking': {
      return getActionNameChangeTimeTracking(event)
    }
    case 'repeat-template': {
      return getActionNameChangeRepeatTemplate(event)
    }
    case 'timer': {
      return getActionNameChangeTimer(event)
    }
    case 'stopwatch': {
      return getActionNameChangeStopwatch(event)
    }

    default:
      return '???'
  }
}
